import React, { useCallback } from 'react';
import { getIn } from 'formik';
import { readAsDataURL } from 'promise-file-reader';
import { useDropzone } from 'react-dropzone';

import styles from '../fieldStyles';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import Icon from 'buyside/components/presentation/Icon/icon';

import { getFilepathExtension, getExtensionIcon } from 'buyside/util/fileHelper';

const FileField = ({
  field,
  form: { touched, errors, setFieldValue, isSubmitting },
  label,
  filefieldConfig,
  disabled
}) => {
  const { filename } = field.value || {};
  const isTouched = getIn(touched, `${field.name}.filename`);
  const error = getIn(errors, `${field.name}.filename`);

  const acceptOptions = filefieldConfig || {
    'text/csv': ['.csv'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'image/*': ['.jpg', '.jpeg', '.png'],
    'application/pdf': ['.pdf'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
  };

  const acceptableExtensions = Object.values(acceptOptions).reduce(
    (memo, arr) => Array.from(new Set([...memo, ...arr.map(el => el)])),
    []
  );

  const handleFileUpload = useCallback(
    async files => {
      const file = files[0];
      const result = await readAsDataURL(file);
      setFieldValue(`${field.name}.filename`, file.name);
      setFieldValue(`${field.name}.base64`, result);
    },
    [field.name, setFieldValue]
  );

  const handleFileDelete = () => {
    setFieldValue(`${field.name}.filename`, '');
    setFieldValue(`${field.name}.base64`, '');
  };

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: acceptOptions,
    onDrop: handleFileUpload
  });

  return (
    <div className={styles.fieldContainer}>
      <label className={styles.fieldLabel}>{label}</label>
      <>
        <div {...getRootProps({ className: styles.dropzone })}>
          <input {...getInputProps()} />
          {isDragAccept && <span>File will be accepted</span>}
          {isDragReject && <span>File will be rejected</span>}
          {!isDragActive && <span>Drag and drop a file here, or click to select one</span>}
        </div>
        {filename && !disabled && (
          <div className={styles.filename}>
            <div>
              <Icon
                type={getExtensionIcon(getFilepathExtension(filename))}
                style={styles.fileTypeIcon}
              />
              {filename}
            </div>
            <Button
              onClick={handleFileDelete}
              className={`${styles.fileButton} deleteButton`}
              text="Remove File"
              size={BUTTON_SIZES.SMALL}
              buttonType={BUTTON_TYPES.BUTTON}
              disabled={disabled || isDragActive}
              isLoading={isSubmitting}
            />
          </div>
        )}
        {!filename && (
          <div className={styles.fieldNote}>
            Accepted file types: {acceptableExtensions.join(', ')}
          </div>
        )}
      </>
      {isTouched && error && <div className={styles.fieldError}>{error}</div>}
    </div>
  );
};

export default FileField;
